import { ref, watch, computed } from 'vue';
import {
  themes,
  themesByName,
  DEFAULT_THEME,
  DEFAULT_MODE,
  type ThemeName,
  type ThemeMode,
} from '../themes';

const STORAGE_THEME = 'demo_theme';
const STORAGE_MODE = 'demo_mode';

function isValidTheme(value: unknown): value is ThemeName {
  return value === 'atelier' || value === 'premium' || value === 'chantier';
}

function isValidMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

function detectInitialTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const stored = localStorage.getItem(STORAGE_THEME);
  return isValidTheme(stored) ? stored : DEFAULT_THEME;
}

function detectInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  const stored = localStorage.getItem(STORAGE_MODE);
  if (isValidMode(stored)) return stored;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : DEFAULT_MODE;
}

// State global (singleton — un seul état de theme/mode pour toute l'app)
const currentTheme = ref<ThemeName>(detectInitialTheme());
const currentMode = ref<ThemeMode>(detectInitialMode());

function applyToDOM(theme: ThemeName, mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-mode', mode);
  // color-scheme natif pour scrollbar / form controls UA
  document.documentElement.style.colorScheme = mode;
  // Sync de la barre d'adresse Chrome Android / iOS Safari sur la couleur
  // de fond du theme actif (sinon Chrome met un bleu vif par defaut).
  // On differe au prochain tick pour que les CSS vars soient resolues.
  requestAnimationFrame(() => {
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue('--ds-color-surface-bg')
      .trim();
    if (bg) {
      // Retire toutes les <meta name="theme-color"> existantes (en a 2 dans
      // index.html : default dark + media light) et n'en garde qu'une avec
      // la couleur du theme actif (plus precis que les media queries).
      document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.remove());
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = bg;
      document.head.appendChild(meta);
    }
  });
}

// Application initiale au chargement du module
applyToDOM(currentTheme.value, currentMode.value);

// Sync sur changements
watch([currentTheme, currentMode], ([theme, mode]) => {
  applyToDOM(theme, mode);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_THEME, theme);
    localStorage.setItem(STORAGE_MODE, mode);
  }
});

export function useTheme() {
  const setTheme = (name: ThemeName) => {
    currentTheme.value = name;
  };

  const setMode = (mode: ThemeMode) => {
    currentMode.value = mode;
  };

  const toggleMode = () => {
    currentMode.value = currentMode.value === 'light' ? 'dark' : 'light';
  };

  const reset = () => {
    currentTheme.value = DEFAULT_THEME;
    currentMode.value = DEFAULT_MODE;
  };

  const currentMetadata = computed(() => themesByName[currentTheme.value]);

  return {
    currentTheme,
    currentMode,
    currentMetadata,
    availableThemes: themes,
    setTheme,
    setMode,
    toggleMode,
    reset,
  };
}
